import { inject } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { of, switchMap } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { LookupService } from "../../../core/services/lookup.service";
import { OtpService } from "../../../core/services/otp.service";
import { RegisterService } from "../../../core/services/register.service";
import { ErrorDialogComponent } from "../../../shared/components/error-dialog/error-dialog.component";
import { IntentTypes } from "../../../shared/enums";
import {
  LookupApiResponse,
  RequestOtpApiResponse,
} from "../../../shared/models/api.response";
import {
  LookupDTO,
  RequestOtpViaMsisdnDTO,
  ValidateOtpDTO,
} from "../../../shared/models/dto.models";
import { RegistrationData } from "../registration.models";
import { registrationActions } from "./actions";
import { HttpResponse } from "@angular/common/http";

//Lookup effects
export const registrationLookUpEffect = createEffect(
  (
    actions$ = inject(Actions),
    lookupService: LookupService = inject(LookupService)
  ) => {
    return actions$.pipe(
      ofType(registrationActions.requestLookup),
      switchMap((dto: LookupDTO) => {
        return lookupService.lookup(dto).pipe(
          map((data: LookupApiResponse) => {
            if (data.intent !== IntentTypes.empty) {
              if (data.intent === IntentTypes.register) {
                return registrationActions.requestLookupSuccess({
                  ...data,
                  intent: data.intent,
                  lookupData: dto,
                });
              } else {
                return registrationActions.requestLookupSuccess({
                  intent: data.intent,
                  error: data.error,
                });
              }
            } else {
              return registrationActions.requestLookupFailed({
                error: data.error,
              });
            }
          }),
          catchError((error) => {
            return of(
              registrationActions.requestLookupFailed({
                error: "Something went wrong. Please try again later.",
              })
            );
          })
        );
      })
    );
  },
  { functional: true }
);

export const registrationLookUpEffectFailed = createEffect(
  (actions$ = inject(Actions), dialog = inject(MatDialog)) => {
    return actions$.pipe(
      ofType(registrationActions.requestLookupFailed),
      map((data) => {
        dialog.open(ErrorDialogComponent, {
          data: {
            errorMessage: data.error,
          },
        });
      })
    );
  },

  { dispatch: false, functional: true }
);

//Request OTP effects
export const registrationRequestOtpEffect = createEffect(
  (actions$ = inject(Actions), otpService: OtpService = inject(OtpService)) => {
    return actions$.pipe(
      ofType(registrationActions.requestOTPViaMsisdn),
      switchMap((dto: RequestOtpViaMsisdnDTO) => {
        return otpService.requestOtp(dto).pipe(
          map((response: HttpResponse<RequestOtpApiResponse>) => {
            if (response?.status === 204) {
              return registrationActions.requestOTPViaMsisdnSuccess({
                msisdn: dto.msisdn,
              });
            } else {
              return registrationActions.requestOTPViaMsisdnFailed({
                error: `Could not send OTP to number: ${dto.msisdn}`,
                msisdn: "",
              });
            }
          }),
          catchError((error) => {
            return of(
              registrationActions.requestOTPViaMsisdnFailed({
                error:
                  error.error.message ??
                  `Could not send OTP to number: ${dto.msisdn}`,
                msisdn: "",
              })
            );
          })
        );
      })
    );
  },
  { functional: true }
);

export const registrationRequestOtpFailedEffect = createEffect(
  (actions$ = inject(Actions), dialog = inject(MatDialog)) => {
    return actions$.pipe(
      ofType(registrationActions.requestOTPViaMsisdnFailed),
      map((data) => {
        dialog.open(ErrorDialogComponent, {
          data: {
            errorMessage: data.error,
          },
        });
      })
    );
  },

  { dispatch: false, functional: true }
);

//Validate OTP effects
export const registrationValidateOtpEffect = createEffect(
  (actions$ = inject(Actions), otpService: OtpService = inject(OtpService)) => {
    return actions$.pipe(
      ofType(registrationActions.requestOTPValidation),
      switchMap((dto: ValidateOtpDTO) => {
        return otpService.validateOtp(dto).pipe(
          map((response) => {
            if (response?.match) {
              return registrationActions.requestOTPValidationSuccess({
                msisdn: dto.msisdn,
                otp: dto.otp,
                match: true,
              });
            } else {
              return registrationActions.requestOTPValidationFailed({
                message: `OTP is Incorrect`,
                msisdn: dto.msisdn,
                otp: "",
              });
            }
          }),
          catchError((error) =>
            of(
              registrationActions.requestOTPValidationFailed({
                message: error.error.message ?? `OTP is Incorrect`,
                msisdn: dto.msisdn,
                otp: "",
              })
            )
          )
        );
      })
    );
  },
  { functional: true }
);

export const registrationValidatetOtpFailedEffect = createEffect(
  (actions$ = inject(Actions), dialog = inject(MatDialog)) => {
    return actions$.pipe(
      ofType(registrationActions.requestOTPValidationFailed),
      map((data) => {
        dialog.open(ErrorDialogComponent, {
          data: {
            errorMessage: data.message,
          },
        });
      })
    );
  },

  { dispatch: false, functional: true }
);

//Register effects
export const registrationRequestRegisterEffect = createEffect(
  (
    actions$ = inject(Actions),
    registerService: RegisterService = inject(RegisterService)
  ) => {
    return actions$.pipe(
      ofType(registrationActions.requestRegistration),
      switchMap((dto: RegistrationData) => {
        return registerService.register(dto).pipe(
          map((response) => {
            if (response?.cardNumber !== "") {
              return registrationActions.requestRegistrationSuccess({
                cardNumber: response.cardNumber,
              });
            } else {
              return registrationActions.requestRegistrationFailed({
                message: "Could not register user",
              });
            }
          }),
          catchError((error) =>
            of(
              registrationActions.requestRegistrationFailed({
                message: error.error.message ?? "Could not register user",
              })
            )
          )
        );
      })
    );
  },
  { functional: true }
);

export const registrationRequestRegisterFailedEffect = createEffect(
  (actions$ = inject(Actions), dialog = inject(MatDialog)) => {
    return actions$.pipe(
      ofType(registrationActions.requestRegistrationFailed),
      map((data) => {
        dialog.open(ErrorDialogComponent, {
          data: {
            errorMessage: data.message,
          },
        });
      })
    );
  },

  { dispatch: false, functional: true }
);
